// The two server-owned limits the cart gates on, resolved from the settings
// payload. Pure functions so the fail-closed behaviour can be tested without
// rendering the page - the component only wires them up.
//
// Both return null when settings are absent or unusable. Nothing here
// substitutes a number: a local guess that disagrees with the server is exactly
// the duplication these replaced, and a failed settings request leaves it wrong
// indefinitely rather than briefly.

const positiveInt = (value) => (Number.isInteger(value) && value > 0 ? value : null)

// Thresholds are non-negative: 0 is a real, documented setting meaning every
// non-zero change goes to approval. Treating it as unusable would show no
// approval warning and label the button Checkout while the server queued
// everything.
const nonNegativeInt = (value) => (Number.isInteger(value) && value >= 0 ? value : null)

/**
 * Per-line hard cap. null means unknown, and the caller must block checkout -
 * the server enforces this and would reject the submit anyway.
 */
export const resolveHardCap = (settings) => positiveInt(settings?.maxDeltaPerLine)

/**
 * Highest auto-approval threshold across the user's roles. null means unknown,
 * in which case the cart shows no approval warning rather than guessing one -
 * the server re-evaluates the threshold on submit regardless.
 */
export const resolveThreshold = (settings, roles) => {
  if (!settings) return null
  const byRole = {
    Sales: settings.thresholdSeatsSales,
    ITManager: settings.thresholdSeatsITManager,
    SSE: settings.thresholdSeatsSSE,
    Admin: settings.thresholdSeatsAdmin,
  }
  const candidates = (roles || [])
    .map((r) => nonNegativeInt(byRole[r]))
    .filter((v) => v !== null)
  return candidates.length > 0 ? Math.max(...candidates) : null
}

/**
 * Lines whose ADD exceeds the cap. Empty when the cap is unknown, so callers
 * never compare against null - `delta > null` is `delta > 0`, which would flag
 * every add and render "you can add up to null seats".
 */
export const linesOverCap = (lines, hardCap) =>
  hardCap === null
    ? []
    : (lines || []).filter((l) => l.requestedQuantity - l.previousQuantity > hardCap)

/**
 * Lines needing approval, on ABSOLUTE delta so a large removal routes there too.
 * Empty when the threshold is unknown.
 */
export const linesOverThreshold = (lines, threshold) =>
  threshold === null
    ? []
    : (lines || []).filter((l) => Math.abs(l.requestedQuantity - l.previousQuantity) > threshold)

/**
 * Whether the basket may be submitted. An unknown cap blocks it: the limit the
 * server enforces is unknown, so nothing may be committed against it.
 */
export const canCheckout = ({ hardCap, overCapCount, blockedCount }) =>
  hardCap !== null && overCapCount === 0 && blockedCount === 0
